import type { components } from '@multaqa/api-client'
import { useLocale } from '@multaqa/i18n'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiOrigin } from '@/lib/api'
import { downloadFile } from '@/lib/download'

export type DataImport = components['schemas']['DataImportResource']
export type DataImportSection = components['schemas']['DataImportSectionResource']
export type DataImportReport = components['schemas']['DataImportReportResource']

/** Statuses a background job is still working on: the screen polls these. */
export function isWorking(status: string | undefined): boolean {
  return status === 'validating' || status === 'applying'
}

const POLL_MS = 3000

function path(groupId: string, companyId: string) {
  return { group: groupId, company: companyId }
}

/** The report is rendered in the request's language, so the key carries it. */
function key(groupId: string, companyId: string, locale: string, ...rest: string[]) {
  return ['platform', 'groups', groupId, 'companies', companyId, 'data-imports', locale, ...rest]
}

function dataOrThrow<T>(body: { data: T } | undefined, endpoint: string): T {
  if (!body) throw new Error(`${endpoint} returned no body`)
  return body.data
}

export function useImportSections(groupId: string, companyId: string) {
  const { locale } = useLocale()
  return useQuery({
    queryKey: key(groupId, companyId, locale, 'sections'),
    queryFn: async (): Promise<DataImportSection[]> => {
      const result = await api.GET(
        '/platform/groups/{group}/companies/{company}/data-imports/sections',
        {
          params: { path: path(groupId, companyId) },
        },
      )
      if (result.error) throw result.error
      return dataOrThrow(result.data, 'GET data-imports/sections')
    },
  })
}

export function useDataImports(groupId: string, companyId: string) {
  const { locale } = useLocale()
  return useQuery({
    queryKey: key(groupId, companyId, locale, 'list'),
    queryFn: async (): Promise<DataImport[]> => {
      const result = await api.GET('/platform/groups/{group}/companies/{company}/data-imports', {
        params: { path: path(groupId, companyId) },
      })
      if (result.error) throw result.error
      return dataOrThrow(result.data, 'GET data-imports')
    },
    refetchInterval: (query) =>
      query.state.data?.some((item) => isWorking(item.status)) ? POLL_MS : false,
  })
}

export function useDataImport(groupId: string, companyId: string, importId: string | null) {
  const { locale } = useLocale()
  return useQuery({
    queryKey: key(groupId, companyId, locale, 'show', importId ?? ''),
    enabled: importId !== null,
    queryFn: async (): Promise<DataImport> => {
      const result = await api.GET(
        '/platform/groups/{group}/companies/{company}/data-imports/{import}',
        {
          params: { path: { ...path(groupId, companyId), import: importId ?? '' } },
        },
      )
      if (result.error) throw result.error
      return dataOrThrow(result.data, 'GET data-imports/{import}')
    },
    refetchInterval: (query) => (isWorking(query.state.data?.status) ? POLL_MS : false),
  })
}

function useRefreshImports(groupId: string, companyId: string) {
  const client = useQueryClient()
  return () =>
    client.invalidateQueries({
      queryKey: ['platform', 'groups', groupId, 'companies', companyId, 'data-imports'],
    })
}

export function useUploadImport(groupId: string, companyId: string) {
  const refresh = useRefreshImports(groupId, companyId)
  return useMutation({
    mutationFn: async (file: File): Promise<DataImport> => {
      const result = await api.POST('/platform/groups/{group}/companies/{company}/data-imports', {
        params: { path: path(groupId, companyId) },
        body: { file: file as unknown as string },
        bodySerializer: () => {
          const form = new FormData()
          form.append('file', file)
          return form
        },
      })
      if (result.error) throw result.error
      return dataOrThrow(result.data, 'POST data-imports')
    },
    onSuccess: () => void refresh(),
  })
}

export function useImportAction(groupId: string, companyId: string, action: 'apply' | 'cancel') {
  const refresh = useRefreshImports(groupId, companyId)
  return useMutation({
    mutationFn: async (importId: string): Promise<DataImport> => {
      const params = { path: { ...path(groupId, companyId), import: importId } }
      const result =
        action === 'apply'
          ? await api.POST(
              '/platform/groups/{group}/companies/{company}/data-imports/{import}/apply',
              { params },
            )
          : await api.POST(
              '/platform/groups/{group}/companies/{company}/data-imports/{import}/cancel',
              { params },
            )
      if (result.error) throw result.error
      return dataOrThrow(result.data, `POST data-imports/{import}/${action}`)
    },
    onSuccess: () => void refresh(),
  })
}

export function downloadImportTemplate(
  groupId: string,
  companyId: string,
  sections: string[],
  locale: string,
): Promise<void> {
  const query = sections.map((section) => `sections[]=${encodeURIComponent(section)}`).join('&')
  return downloadFile(
    `${apiOrigin}/api/platform/groups/${groupId}/companies/${companyId}/data-imports/template?${query}`,
    'data-import-template.xlsx',
    { 'Accept-Language': locale },
  )
}
